import { create } from 'zustand'

export interface SnackbarMsg { id: number; message: string; actionLabel?: string; onAction?: () => void; severity?: 'info' | 'error' | 'success'; durationMs?: number }

export interface ConfirmRequest { title: string; body?: string; confirmLabel?: string; danger?: boolean; resolve: (ok: boolean) => void }
export interface AskRequest { title: string; label: string; initial?: string; confirmLabel?: string; validate?: (v: string) => string | null; resolve: (v: string | null) => void }
export interface LabelDialogState { label?: { id: string; name: string; color: string; parentId: string | null }; parentId?: string | null }

export interface ComposeState {
  key: string // idempotency + window identity
  draftId: string | null
  to: string[]; cc: string[]; bcc: string[]
  subject: string
  html: string
  inReplyTo: string | null
  references: string[]
  attachments: Array<{ id: string; filename: string; size: number; contentType: string }>
  minimized: boolean
  maximized: boolean
  outboxId: string | null
  status: 'editing' | 'sending' | 'sent' | 'failed'
  error?: string
}

interface UiState {
  selected: Set<string>
  focusedId: string | null
  toggleSelected: (id: string) => void
  setSelected: (ids: string[]) => void
  clearSelection: () => void
  setFocused: (id: string | null) => void

  composes: ComposeState[]
  openCompose: (init?: Partial<ComposeState>) => string
  updateCompose: (key: string, patch: Partial<ComposeState>) => void
  closeCompose: (key: string) => void

  snackbars: SnackbarMsg[]
  notify: (m: Omit<SnackbarMsg, 'id'>) => void
  dismiss: (id: number) => void

  sidebarCollapsed: boolean
  toggleSidebar: () => void
  confirmReq: ConfirmRequest | null
  askReq: AskRequest | null
  labelDialog: LabelDialogState | null
  setConfirm: (r: ConfirmRequest | null) => void
  setAsk: (r: AskRequest | null) => void
  setLabelDialog: (s: LabelDialogState | null) => void
  drawerOpen: boolean
  setDrawerOpen: (v: boolean) => void
  shortcutsHelp: boolean
  setShortcutsHelp: (v: boolean) => void
}

let seq = 0
const MAX_COMPOSE = 3

export const useUi = create<UiState>((set) => ({
  selected: new Set(),
  focusedId: null,
  toggleSelected: (id) => set((s) => { const n = new Set(s.selected); if (n.has(id)) n.delete(id); else n.add(id); return { selected: n } }),
  setSelected: (ids) => set({ selected: new Set(ids) }),
  clearSelection: () => set({ selected: new Set() }),
  setFocused: (id) => set({ focusedId: id }),

  composes: [],
  openCompose: (init = {}) => {
    const key = init.key ?? crypto.randomUUID()
    set((s) => ({
      composes: [
        ...s.composes.filter((c) => c.key !== key).slice(-(MAX_COMPOSE - 1)),
        { key, draftId: null, to: [], cc: [], bcc: [], subject: '', html: '', inReplyTo: null, references: [], attachments: [], minimized: false, maximized: false, outboxId: null, status: 'editing', ...init },
      ],
    }))
    return key
  },
  updateCompose: (key, patch) => set((s) => ({ composes: s.composes.map((c) => (c.key === key ? { ...c, ...patch } : c)) })),
  closeCompose: (key) => set((s) => ({ composes: s.composes.filter((c) => c.key !== key) })),

  snackbars: [],
  notify: (m) => set((s) => ({ snackbars: [...s.snackbars, { ...m, id: ++seq }] })),
  dismiss: (id) => set((s) => ({ snackbars: s.snackbars.filter((x) => x.id !== id) })),

  sidebarCollapsed: (() => { try { return localStorage.getItem('sidebar') === 'collapsed' } catch { return false } })(),
  toggleSidebar: () => set((st) => { const v = !st.sidebarCollapsed; try { localStorage.setItem('sidebar', v ? 'collapsed' : 'open') } catch { /* private mode */ } return { sidebarCollapsed: v } }),
  confirmReq: null,
  askReq: null,
  labelDialog: null,
  setConfirm: (r) => set({ confirmReq: r }),
  setAsk: (r) => set({ askReq: r }),
  setLabelDialog: (st) => set({ labelDialog: st }),
  drawerOpen: false,
  setDrawerOpen: (v) => set({ drawerOpen: v }),
  shortcutsHelp: false,
  setShortcutsHelp: (v) => set({ shortcutsHelp: v }),
}))
